<template>
  <v-dialog v-model="internalVisible" max-width="640" persistent transition="dialog-bottom-transition">
    <v-card outlined class="modern-card overflow-hidden">
      <v-sheet color="primary" class="pa-5 d-flex align-center">
        <v-avatar size="40" color="white opacity-20" class="mr-3">
          <v-icon color="white" small>fas fa-receipt</v-icon>
        </v-avatar>
        <div>
          <div class="subtitle-1 white--text font-weight-black">Emitir Boleta Electrónica</div>
          <div class="caption white--text opacity-70">
            Tipo 39 · Contado · {{ providerLabel }}
            <span v-if="otId"> · OT #{{ otId }}</span>
          </div>
        </div>
        <v-spacer />
        <v-btn icon dark @click="close"><v-icon small>fas fa-times</v-icon></v-btn>
      </v-sheet>

      <v-card-text class="pa-6">
        <v-alert v-if="!providerReady" type="warning" text dense class="mb-4">
          No hay proveedor de facturación electrónica habilitado (Relbase o Simple Factura).
        </v-alert>

        <v-alert v-if="blockReason" type="error" text dense class="mb-4">
          {{ blockReason }}
        </v-alert>

        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <template v-else-if="issuedResult">
          <v-alert type="success" text prominent class="mb-4">
            <div class="subtitle-1 font-weight-black">Boleta emitida</div>
            <div class="body-2 mt-1">
              Folio <strong>{{ issuedResult.folio || '—' }}</strong>
              <span v-if="issuedResult.total"> · {{ issuedResult.total | currency('$', 0) }}</span>
            </div>
          </v-alert>
          <p class="body-2 grey--text mb-0">
            Puedes abrir el PDF ahora o cerrar y consultarlo después en documentos emitidos.
          </p>
        </template>

        <template v-else-if="providerReady">
          <!-- Standalone: OT opcional + cliente -->
          <v-row dense v-if="!otId">
            <v-col cols="12" md="5">
              <v-text-field
                v-model="otSearch"
                label="OT (opcional)"
                outlined dense hide-details
                placeholder="N° OT"
                @keydown.enter.prevent="loadOt"
              />
            </v-col>
            <v-col cols="12" md="3" class="d-flex align-center">
              <v-btn color="primary" outlined dense :loading="loadingOt" @click="loadOt">Cargar</v-btn>
              <v-btn v-if="linkedOtId" text dense class="ml-1" @click="clearOt">Quitar</v-btn>
            </v-col>
            <v-col cols="12">
              <div class="caption grey--text font-weight-bold mb-1">Cliente</div>
              <CustomerComplete2 v-model="selectedCustomer" @input="onCustomerSelected" />
            </v-col>
          </v-row>

          <v-card outlined class="pa-4 mb-4 bg-grey-light" v-if="customer.id">
            <div class="caption grey--text font-weight-black text-uppercase mb-1">Receptor</div>
            <div class="body-2 font-weight-black">{{ customer.rut }} · {{ customer.name }}</div>
            <div class="caption grey--text" v-if="customer.giro">{{ customer.giro }}</div>
          </v-card>

          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="totalBruto"
                type="number"
                label="Total boleta (con IVA)"
                outlined dense
                prefix="$"
                min="1"
                step="1"
                hide-details="auto"
                :error-messages="totalError"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="paymentMethod"
                :items="paymentMethodItems"
                :item-text="paymentItemText"
                :item-value="paymentItemValue"
                label="Medio de pago"
                outlined dense
                hide-details="auto"
                :error-messages="paymentError"
              />
            </v-col>
          </v-row>

          <v-row dense class="mt-2" v-if="isRelbase">
            <v-col cols="12" md="4">
              <v-select
                v-model="vendedor"
                :items="relbaseSellers"
                item-text="name"
                item-value="relbase_id"
                label="Vendedor Relbase"
                outlined dense hide-details
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="bodega"
                :items="relbaseWarehouses"
                item-text="name"
                item-value="relbase_id"
                label="Bodega"
                outlined dense hide-details
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="canalVenta"
                :items="relbaseChannels"
                item-text="name"
                item-value="relbase_id"
                label="Canal de venta"
                outlined dense hide-details
              />
            </v-col>
          </v-row>

          <v-textarea
            v-model="glosa"
            label="Glosa / observación"
            outlined dense
            rows="2"
            class="mt-3"
            hide-details
            placeholder="Detalle de la venta..."
          />

          <v-card outlined class="pa-4 mt-4">
            <div class="d-flex justify-space-between caption">
              <span>Neto</span>
              <strong>{{ neto | currency('$', 0) }}</strong>
            </div>
            <div class="d-flex justify-space-between caption mt-1">
              <span>IVA ({{ taxRate }}%)</span>
              <strong>{{ ivaAmount | currency('$', 0) }}</strong>
            </div>
            <v-divider class="my-2" />
            <div class="d-flex justify-space-between body-1 font-weight-black primary--text">
              <span>Total</span>
              <span>{{ displayTotal | currency('$', 0) }}</span>
            </div>
          </v-card>
        </template>
      </v-card-text>

      <v-card-actions class="px-6 pb-5">
        <template v-if="issuedResult">
          <v-btn text class="font-weight-bold" @click="finishIssued">Cerrar</v-btn>
          <v-spacer />
          <v-btn
            v-if="canOpenPdf"
            color="primary"
            depressed
            class="font-weight-black px-6"
            :loading="openingPdf"
            @click="openIssuedPdf"
          >
            <v-icon left small>fas fa-file-pdf</v-icon>
            Ver PDF ahora
          </v-btn>
        </template>
        <template v-else>
          <v-btn text class="font-weight-bold" @click="close">Cancelar</v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            depressed
            class="font-weight-black px-6"
            :loading="submitting"
            :disabled="!canSubmit"
            @click="submit"
          >
            <v-icon left small>fas fa-paper-plane</v-icon>
            Emitir boleta
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import CustomerComplete2 from '@/components/customers/CustomerComplete2.vue'
import { getWorkOrderforDTE } from '@/api/salesNotes'
import { createDocument, downloadDocumentPdf } from '@/api/simplefactura'
import {
  fetchRelbasePaymentMethods,
  fetchRelbaseSellers,
  fetchRelbaseWarehouses,
  fetchRelbaseSaleChannels,
  issueRelbaseDTE,
  syncCustomerRelbase,
} from '@/api/relbase'
import notifications from '@/utils/notifications'

const GENERIC_PAYMENT_METHODS = [
  { value: 'efectivo', text: 'Efectivo' },
  { value: 'transferencia', text: 'Transferencia' },
  { value: 'tarjeta', text: 'Tarjeta' },
  { value: 'cheque', text: 'Cheque' },
  { value: 'otro', text: 'Otro' },
]

function uuidv4() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export default {
  name: 'BoletaIssuanceDialog',
  components: { CustomerComplete2 },
  props: {
    value: { type: Boolean, default: false },
    otId: { type: [Number, String], default: null },
    workOrder: { type: Object, default: null },
  },
  data() {
    return {
      loading: false,
      loadingOt: false,
      submitting: false,
      otSearch: '',
      linkedOtId: null,
      selectedCustomer: null,
      customer: {},
      totalBruto: null,
      paymentMethod: null,
      glosa: '',
      totalError: '',
      paymentError: '',
      blockReason: '',
      relbasePaymentMethods: [],
      relbaseSellers: [],
      relbaseWarehouses: [],
      relbaseChannels: [],
      vendedor: null,
      bodega: null,
      canalVenta: null,
      issuedResult: null,
      openingPdf: false,
    }
  },
  computed: {
    canOpenPdf() {
      return this.isSimpleFactura && this.issuedResult && this.issuedResult.id
    },
    internalVisible: {
      get() { return this.value },
      set(v) { this.$emit('input', v) },
    },
    company() {
      return this.$store.getters.getCompany || {}
    },
    isSimpleFactura() {
      return !!this.company.simplefactura_integration_enabled
    },
    isRelbase() {
      return !this.isSimpleFactura && !!this.company.relbase_integration_enabled
    },
    providerReady() {
      return this.isSimpleFactura || this.isRelbase
    },
    providerLabel() {
      if (this.isSimpleFactura) return 'Simple Factura'
      if (this.isRelbase) return 'Relbase'
      return 'Sin proveedor'
    },
    taxRate() {
      return parseFloat(this.company.tax) || 19
    },
    displayTotal() {
      return Math.max(0, Math.round(parseFloat(this.totalBruto) || 0))
    },
    neto() {
      if (!this.displayTotal) return 0
      return Math.round(this.displayTotal / (1 + this.taxRate / 100))
    },
    ivaAmount() {
      return this.displayTotal - this.neto
    },
    paymentMethodItems() {
      if (this.isRelbase) return this.relbasePaymentMethods
      return GENERIC_PAYMENT_METHODS
    },
    paymentItemText() {
      return this.isRelbase ? 'name' : 'text'
    },
    paymentItemValue() {
      return this.isRelbase ? 'relbase_id' : 'value'
    },
    effectiveOtId() {
      return this.otId || this.linkedOtId
    },
    canSubmit() {
      return this.providerReady
        && !this.blockReason
        && this.customer && this.customer.id
        && this.displayTotal > 0
        && !!this.paymentMethod
        && !this.submitting
    },
  },
  watch: {
    value(val) {
      if (val) this.bootstrap()
    },
  },
  methods: {
    close() {
      this.internalVisible = false
    },
    finishIssued() {
      this.close()
    },
    async openIssuedPdf() {
      if (!this.canOpenPdf || this.openingPdf) return
      this.openingPdf = true
      try {
        await downloadDocumentPdf(this.issuedResult.id)
      } catch (e) {
        notifications.showError(e || 'No se pudo abrir el PDF')
      } finally {
        this.openingPdf = false
      }
    },
    async bootstrap() {
      this.loading = true
      this.blockReason = ''
      this.issuedResult = null
      this.openingPdf = false
      this.totalError = ''
      this.paymentError = ''
      this.totalBruto = null
      this.paymentMethod = null
      this.glosa = ''
      this.linkedOtId = this.otId || null
      this.otSearch = this.otId ? String(this.otId) : ''
      this.customer = {}
      this.selectedCustomer = null
      try {
        if (this.isRelbase) await this.loadRelbaseCatalogs()
        if (this.otId) await this.loadOtById(this.otId)
        else if (this.workOrder && this.workOrder.id) await this.loadOtById(this.workOrder.id)
      } catch (e) {
        notifications.showError(e)
      } finally {
        this.loading = false
      }
    },
    async loadRelbaseCatalogs() {
      const [pm, sellers, warehouses, channels] = await Promise.all([
        fetchRelbasePaymentMethods().catch(() => []),
        fetchRelbaseSellers().catch(() => []),
        fetchRelbaseWarehouses().catch(() => []),
        fetchRelbaseSaleChannels().catch(() => []),
      ])
      this.relbasePaymentMethods = Array.isArray(pm) ? pm : (pm.results || [])
      this.relbaseSellers = Array.isArray(sellers) ? sellers : (sellers.results || [])
      this.relbaseWarehouses = Array.isArray(warehouses) ? warehouses : (warehouses.results || [])
      this.relbaseChannels = Array.isArray(channels) ? channels : (channels.results || [])
      if (this.relbaseSellers.length && !this.vendedor) this.vendedor = this.relbaseSellers[0].relbase_id
      if (this.relbaseWarehouses.length && !this.bodega) this.bodega = this.relbaseWarehouses[0].relbase_id
      if (this.relbaseChannels.length && !this.canalVenta) this.canalVenta = this.relbaseChannels[0].relbase_id
    },
    async loadOt() {
      if (!this.otSearch) {
        notifications.showError('Indique el número de OT')
        return
      }
      this.loadingOt = true
      try {
        await this.loadOtById(this.otSearch)
      } finally {
        this.loadingOt = false
      }
    },
    clearOt() {
      this.linkedOtId = null
      this.otSearch = ''
      this.blockReason = ''
    },
    async loadOtById(id) {
      const data = await getWorkOrderforDTE(id)
      this.linkedOtId = data.id
      this.customer = data.customer || {}
      this.selectedCustomer = this.customer
      this.glosa = data.title || `OT #${data.id}`
      const total = data.totals && (data.totals.total || data.totals.neto)
      if (total) this.totalBruto = Math.round(parseFloat(total))
      this.checkExclusivity(data)
      if (this.isRelbase) {
        const sellerId = data.customer && data.customer.seller && data.customer.seller.relbase_seller_id
        if (sellerId) this.vendedor = sellerId
      }
    },
    checkExclusivity(data) {
      this.blockReason = ''
      if (data && data.has_factura) {
        this.blockReason = 'Esta OT ya tiene factura. Factura y boleta son excluyentes.'
        return
      }
      if (data && data.has_boleta) {
        this.blockReason = 'Esta OT ya tiene una boleta emitida.'
        return
      }

      const relbaseDocs = (data && data.relbase_documents) || []
      const sfDocs = (data && data.simplefactura_documents) || []
      const dtes = (data && data.dtes) || []
      const hasFactura =
        relbaseDocs.some(d => Number(d.doc_type) === 33) ||
        sfDocs.some(d => Number(d.tipo) === 33) ||
        dtes.some(d => d.tipo === 30 || d.tipo === 33)
      const hasBoleta =
        relbaseDocs.some(d => Number(d.doc_type) === 39) ||
        sfDocs.some(d => Number(d.tipo) === 39) ||
        dtes.some(d => Number(d.tipo) === 39)

      if (hasFactura) {
        this.blockReason = 'Esta OT ya tiene factura. Factura y boleta son excluyentes.'
        return
      }
      if (hasBoleta) {
        this.blockReason = 'Esta OT ya tiene una boleta emitida.'
        return
      }

      // Fallback: datos de la OT en el listado
      if (this.workOrder) {
        const rb = this.workOrder.relbase_documents || []
        const sf = this.workOrder.simplefactura_documents || []
        if (rb.some(d => Number(d.doc_type) === 33) || sf.some(d => Number(d.tipo) === 33)) {
          this.blockReason = 'Esta OT ya tiene factura. Factura y boleta son excluyentes.'
        } else if (rb.some(d => Number(d.doc_type) === 39) || sf.some(d => Number(d.tipo) === 39)) {
          this.blockReason = 'Esta OT ya tiene una boleta emitida.'
        }
      }
    },
    onCustomerSelected(customer) {
      this.customer = customer || {}
    },
    paymentMethodName() {
      if (this.isRelbase) {
        const found = this.relbasePaymentMethods.find(p => String(p.relbase_id) === String(this.paymentMethod))
        return found ? found.name : ''
      }
      const found = GENERIC_PAYMENT_METHODS.find(p => p.value === this.paymentMethod)
      return found ? found.text : this.paymentMethod
    },
    validate() {
      this.totalError = ''
      this.paymentError = ''
      if (!this.customer || !this.customer.id) {
        notifications.showError('Debe seleccionar un cliente')
        return false
      }
      if (!this.displayTotal || this.displayTotal <= 0) {
        this.totalError = 'Ingrese el total'
        return false
      }
      if (!this.paymentMethod) {
        this.paymentError = 'Seleccione medio de pago'
        return false
      }
      return true
    },
    async submit() {
      if (!this.validate() || this.blockReason) return
      this.submitting = true
      try {
        const result = this.isSimpleFactura
          ? await this.submitSimpleFactura()
          : await this.submitRelbase()
        notifications.showSuccess(
          result && result.folio
            ? `Boleta emitida. Folio: ${result.folio}`
            : 'Boleta emitida correctamente'
        )
        this.issuedResult = {
          id: result && (result.id || result.document_id || result.sf_id) || null,
          folio: result && result.folio,
          total: (result && result.total != null) ? result.total : this.displayTotal,
          provider: this.isSimpleFactura ? 'simplefactura' : 'relbase',
        }
        this.$emit('issued', this.issuedResult)
        // Abrir PDF de inmediato (igual que factura Simple Factura)
        if (this.isSimpleFactura && this.issuedResult.id) {
          try {
            await downloadDocumentPdf(this.issuedResult.id)
          } catch (err) {
            notifications.showError('La boleta se emitió, pero no se pudo abrir el PDF automáticamente')
          }
        }
      } catch (e) {
        const msg = (e && e.message) || (typeof e === 'string' ? e : null) || (e && e.response && e.response.data && (e.response.data.message || e.response.data.error)) || e
        notifications.showError(msg)
      } finally {
        this.submitting = false
      }
    },
    async submitSimpleFactura() {
      return createDocument({
        tipo: 39,
        workorder_id: this.effectiveOtId || null,
        customer_id: this.customer.id,
        total_bruto: this.displayTotal,
        payment_method: this.paymentMethodName(),
        observacion: this.glosa || 'Venta boleta electrónica',
        iva: this.taxRate,
      })
    },
    async submitRelbase() {
      let relbaseCustomerId = this.customer.relbase_customer_id
      if (!relbaseCustomerId) {
        const synced = await syncCustomerRelbase(this.customer.id, true)
        relbaseCustomerId = synced && (synced.relbase_customer_id || (synced.customer && synced.customer.relbase_customer_id))
      }
      if (!relbaseCustomerId) {
        throw new Error('El cliente no está sincronizado con Relbase')
      }
      const today = new Date().toISOString().slice(0, 10)
      const relbasePayload = {
        type_document: 1001,
        type_document_sii: 39,
        start_date: today,
        end_date: today,
        customer_id: parseInt(relbaseCustomerId),
        address: '',
        is_str_city_and_comune: true,
        str_commune: '',
        str_city: '',
        seller_id: parseInt(this.vendedor) || null,
        ware_house_id: parseInt(this.bodega) || null,
        channel_id: parseInt(this.canalVenta) || null,
        type_payment_id: parseInt(this.paymentMethod) || null,
        comment: [this.effectiveOtId ? `OT-${this.effectiveOtId}` : '', this.glosa || 'Boleta electrónica'].filter(Boolean).join(' - '),
        label_value: 'string',
        type_transfer: 0,
        contact: '',
        paid: true,
        mnt_bruto: false,
        amount_paid: this.displayTotal,
        change_due: 0,
        products: [
          {
            code: 'BOLETA',
            price: this.neto,
            quantity: 1,
            tax_affected: true,
            description: this.glosa || 'Venta boleta electrónica',
            unit_item: 'Unidad',
          },
        ],
      }
      const result = await issueRelbaseDTE({
        request_id: uuidv4(),
        doc_type: 39,
        work_order_id: this.effectiveOtId || null,
        customer_id: this.customer.id,
        relbase_seller_id: this.vendedor || '',
        payment_method_id: String(this.paymentMethod || ''),
        payment_method_name: this.paymentMethodName(),
        relbase_payload: relbasePayload,
      })
      if (result.status === 'error') {
        throw new Error(result.message || 'Error al emitir boleta en Relbase')
      }
      return {
        ...result,
        folio: result.folio,
        id: result.document_id || result.id || null,
        total: this.displayTotal,
      }
    },
  },
}
</script>

<style scoped>
.modern-card { border-radius: 12px !important; }
.bg-grey-light { background-color: #f8f9fa; }
.opacity-20 { background-color: rgba(255,255,255,0.2) !important; }
.opacity-70 { opacity: 0.7; }
</style>
